import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { getUserRole } from "../security/roleManager";
import { ROLES } from "../security/roles";

const TelemetryContext = createContext();

const API_BASE =
  process.env.REACT_APP_API_URL || "http://34.192.63.227:8001";

const PAID_ROLES = [
  ROLES.CUSTOMER_USER,
  ROLES.CUSTOMER_ADMIN,
  ROLES.FENTASAFE_ADMIN,
];

// ─── Simulated data generator (Demo users only) ───────────────────
function generateSimulatedTelemetry() {
  const pm25 = Math.floor(Math.random() * 100);
  const voc = Math.floor(Math.random() * 500);
  const fentanyl_ppb = Math.floor(Math.random() * 10);

  let overallRisk = "SAFE";
  let aiConfidence = 50;

  if (pm25 > 60 || voc > 300) {
    overallRisk = "WARNING";
    aiConfidence = 75;
  }

  if (pm25 > 85 || voc > 420 || fentanyl_ppb > 6) {
    overallRisk = "CRITICAL";
    aiConfidence = 92;
  }

  const explanations = [];
  if (pm25 > 60) explanations.push("pm25Exceeded");
  if (voc > 300) explanations.push("vocExceeded");
  if (fentanyl_ppb > 6) explanations.push("exposureElevated");
  if (explanations.length === 0)
    explanations.push("allNormal");

  return {
    pm25,
    voc,
    fentanyl_ppb,
    overallRisk,
    aiConfidence,
    explanations,
    dataSource: "SIMULATED",
    zones: [
      { zone: "zoneA", risk: overallRisk },
      { zone: "zoneB", risk: overallRisk },
      { zone: "zoneC", risk: overallRisk },
    ],
  };
}

// ─── Real API fetcher (Paid users only) ───────────────────────────
async function fetchLiveTelemetry() {
  try {
    const token = localStorage.getItem("FENTASAFE_TOKEN");

    const res = await fetch(`${API_BASE}/api/ai-telemetry`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) throw new Error(`API error: ${res.status}`);

    const data = await res.json();

    return {
      pm25: data.pm25 ?? 0,
      voc: data.voc ?? 0,
      fentanyl_ppb: data.fentanyl_ppb ?? 0,
      overallRisk: data.overallRisk ?? "SAFE",
      aiConfidence: data.aiConfidence ?? 50,
      explanations: data.explanations ?? ["liveDataActive"],
      dataSource: "LIVE",
      zones: data.zones ?? [],
      // Pass through any extra fields from the backend
      prediction: data.prediction ?? null,
      explanation: data.explanation ?? null,
      decision: data.decision ?? null,
    };
  } catch (err) {
    console.warn("TelemetryContext: Live API unavailable, using last known state.", err);
    return null; // Caller handles null gracefully
  }
}

export function TelemetryProvider({ children }) {
  const role = getUserRole();
  const isLiveUser = PAID_ROLES.includes(role);

  const [telemetry, setTelemetry] = useState({
    pm25: 42,
    voc: 220,
    fentanyl_ppb: 4,
    overallRisk: "SAFE",
    aiConfidence: 50,
    explanations: ["initializing"],
    dataSource: isLiveUser ? "LIVE" : "SIMULATED",
    zones: [],
  });

  useEffect(() => {
    // ── DEMO USER: simulated random data (original behavior) ──────
    if (!isLiveUser) {
      const interval = setInterval(() => {
        setTelemetry(generateSimulatedTelemetry());
      }, 3000);
      return () => clearInterval(interval);
    }

    // ── PAID USER: real backend data ──────────────────────────────
    // Fetch immediately on mount, then poll every 5 seconds
    // (slightly slower than demo to avoid hammering the API)
    let cancelled = false;

    const fetchAndUpdate = async () => {
      const data = await fetchLiveTelemetry();
      if (!cancelled && data) {
        setTelemetry(data);
      }
    };

    fetchAndUpdate(); // immediate first fetch
    const interval = setInterval(fetchAndUpdate, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isLiveUser]);

  return (
    <TelemetryContext.Provider value={telemetry}>
      {children}
    </TelemetryContext.Provider>
  );
}

export function useTelemetry() {
  return useContext(TelemetryContext);
}
